import { useCallback, useEffect, useMemo, useState } from "react";
import { AuthorizationSettingsStore, CustomerApproverSummary } from "./authorizationSettingsStore";
import { CustomerApprovers, EMPTY_CUSTOMER_APPROVERS } from "./customerApprovers";

type Status = "loading" | "ready" | "error";

export interface SaveResult {
  ok: boolean;
  message: string;
}

export interface CustomerApproverSettings {
  approversStatus: (customerId: string) => Status;
  approversFor: (customerId: string) => CustomerApprovers;
  setApprovers: (customerId: string, approvers: CustomerApprovers) => void;
  ensureApproversLoaded: (customerId: string) => void;
  approverSummary: readonly CustomerApproverSummary[];
  isDirty: boolean;
  isSaving: boolean;
  save: () => Promise<SaveResult>;
  discard: () => void;
}

const sameValue = (left: unknown, right: unknown): boolean => JSON.stringify(left) === JSON.stringify(right);

export function errorMessage(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: { error?: unknown } } })?.response?.data;
  if (typeof data?.error === "string" && data.error) return data.error;
  return error instanceof Error && error.message ? error.message : fallback;
}

/**
 * Holds the saved and edited approvers of each customer and saves only the customers that changed.
 * Approvers belong to the customer, so every agent shares them.
 */
export function useCustomerApprovers(store: AuthorizationSettingsStore): CustomerApproverSettings {
  const [statusByCustomer, setStatusByCustomer] = useState<Readonly<Record<string, Status>>>({});
  const [savedApprovers, setSavedApprovers] = useState<Readonly<Record<string, CustomerApprovers>>>({});
  const [editedApprovers, setEditedApprovers] = useState<Readonly<Record<string, CustomerApprovers>>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [approverSummary, setApproverSummary] = useState<readonly CustomerApproverSummary[]>([]);

  const refreshApproverSummary = useCallback(() => {
    // The summary is a convenience list; a failure leaves the last good list in place.
    store
      .loadApproverSummary()
      .then(setApproverSummary)
      .catch(() => undefined);
  }, [store]);

  useEffect(() => {
    refreshApproverSummary();
  }, [refreshApproverSummary]);

  const ensureApproversLoaded = useCallback(
    (customerId: string) => {
      if (!customerId || statusByCustomer[customerId] === "loading" || statusByCustomer[customerId] === "ready") return;
      setStatusByCustomer((current) => ({ ...current, [customerId]: "loading" }));
      store
        .loadApprovers(customerId)
        .then((loaded) => {
          setSavedApprovers((current) => ({ ...current, [customerId]: loaded }));
          setEditedApprovers((current) => ({ ...current, [customerId]: loaded }));
          setStatusByCustomer((current) => ({ ...current, [customerId]: "ready" }));
        })
        .catch(() => setStatusByCustomer((current) => ({ ...current, [customerId]: "error" })));
    },
    [store, statusByCustomer]
  );

  const dirtyCustomerIds = useMemo(
    () => Object.keys(editedApprovers).filter((customerId) => !sameValue(editedApprovers[customerId], savedApprovers[customerId])),
    [editedApprovers, savedApprovers]
  );
  const isDirty = dirtyCustomerIds.length > 0;

  const save = useCallback(async (): Promise<SaveResult> => {
    if (!isDirty || isSaving) return { ok: true, message: "Nothing to save." };
    setIsSaving(true);
    try {
      for (const customerId of dirtyCustomerIds) {
        const stored = await store.saveApprovers(customerId, editedApprovers[customerId]);
        setSavedApprovers((current) => ({ ...current, [customerId]: stored }));
        setEditedApprovers((current) => ({ ...current, [customerId]: stored }));
      }
      refreshApproverSummary();
      return { ok: true, message: "Customer approvers saved." };
    } catch (error) {
      return { ok: false, message: errorMessage(error, "Couldn't save customer approvers. Try again.") };
    } finally {
      setIsSaving(false);
    }
  }, [dirtyCustomerIds, editedApprovers, isDirty, isSaving, refreshApproverSummary, store]);

  return {
    approversStatus: (customerId) => statusByCustomer[customerId] ?? "loading",
    approversFor: (customerId) => editedApprovers[customerId] ?? EMPTY_CUSTOMER_APPROVERS,
    setApprovers: (customerId, approvers) => setEditedApprovers((current) => ({ ...current, [customerId]: approvers })),
    ensureApproversLoaded,
    approverSummary,
    isDirty,
    isSaving,
    save,
    discard: () => setEditedApprovers(savedApprovers),
  };
}
